import React, { useState, useEffect } from 'react';

import { AddOptionInput } from "../index.jsx"
import { Chip } from "../ui"

const MultiSelect = ({
    field,
    form,
    allowAddition,
    defaultOptions = [],
    maxSelections,
    mode = 'multiselect',
    selections = false,
    onSelectionChange,
    value
}) => {
    if (defaultOptions.length > 0 && !defaultOptions[0].id) {
        defaultOptions = defaultOptions.map(function(o) {
            return {
                'id': o,
                'name': o
            }
        })
    }

    value = field ? field.value : value

    if (mode === 'multiselect' && value?.length > 0 && value[0].name) {
        value = value.map(v => v.id)

        form && form.setFieldValue(
            field.name,
            value
        );
    }

    const defaultValue = mode === 'switch' ? null : []
    const defaultSelections = value || defaultValue

    // Add options present in selections but not in default options
    const missingOptions = defaultSelections?.filter ? defaultSelections.filter(
        (s) => !defaultOptions.some((d) => d.id === s)
    ) : []

    const extraOptions = missingOptions.map((o) => { return {id: o, name: o}})

    const [ options, setOptions ] = useState(defaultOptions.concat(extraOptions))

    useEffect(() => {
        if (!defaultOptions?.length) {
            return;
        }

        const normalized = defaultOptions[0]?.id !== undefined
            ? defaultOptions
            : defaultOptions.map((option) => ({ id: option, name: option }));

        setOptions((prev) => {
            const customOptions = prev.filter(
                (option) => !normalized.some((item) => item.id === option.id)
            );

            return normalized.concat(customOptions);
        });
    }, [defaultOptions]);

    let setSelections = () => {}

    // `selections === false` means uncontrolled, the same convention React
    // inputs use. The state for that mode is declared unconditionally — a
    // useState behind the `if` disappeared whenever a caller supplied
    // selections, and a caller that switches modes mid-life would have
    // changed this component's hook count.
    const [ stateSelections, setStateSelections ] = useState(defaultSelections)

    if (selections === false) {
        selections = stateSelections
        setSelections = setStateSelections
    }

    function addOption(o) {
        const option = {id: o, name: o}
        options.push(option)
        selections.push(o)

        setOptions([...options])
        setSelections([...selections])
    }

    const isRemovable = (o) => {
        if (!defaultOptions?.length) {
            return true
        }

        return !defaultOptions.find(_o => _o?.id === o.id)
    }

    function isSelected(o) {
        if (mode === 'switch') {
            return o.id === selections
        }

        return selections.some(s => s === o.id || (s?.id && s.id === o.id));
    }

    // Chip's × stops propagation itself, so the chip's own toggle never fires with it.
    const onRemoveOptionClick = (o) => {
        if (isSelected(o)) {
            const newSelections = selections.filter(s => s !== o.id && (!s.id || s.id !== o.id))

            setSelections(newSelections)
            onSelectionChange && onSelectionChange(newSelections)
        }

        setOptions(options.filter(_o => _o.id !== o.id))
    }

    function handleClick(e, o) {
        e.preventDefault()

        let newSelections;

        if (mode === 'switch') {
            newSelections = o.id
        } else if (isSelected(o)) {
            newSelections = selections.filter(s => s !== o.id && (!s.id || s.id !== o.id))
        } else {
            if (maxSelections && selections.length >= maxSelections) {
                return;
            }

            newSelections = selections.slice(0)
            newSelections.push(o.id)
        }

        form && form.setFieldValue(
            field.name,
            newSelections
        );

        setSelections(newSelections)
        onSelectionChange && onSelectionChange(newSelections)
    }

    let optionElements = options.map((o) => (
        <Chip
            key={o.id}
            selected={isSelected(o)}
            onClick={(e) => handleClick(e, o)}
            onRemove={isRemovable(o) ? () => onRemoveOptionClick(o) : undefined}
        >
            { o.name }
        </Chip>
    ))

    return (
        <div className="multiselect">
            { options.length > 0 && (
                <div className="d-flex flex-wrap gap-2">
                    { optionElements }
                </div>
            )}

            { allowAddition && (
                <AddOptionInput
                    onAdd={(option) => addOption(option)}
                />
            )}
        </div>
    )
};

export default MultiSelect;